import * as React from 'react';
import { Helmet } from 'react-helmet';
import type { PageProps } from 'gatsby';

interface HeadProps {
  title?: string;
  description?: string;
  seoImage?: string;
  location?: PageProps['location'];
}

/**
 *  Truncates a paragraph to a target length for SEO meta description tags
 */
function truncate(text?: string, length = 160) {
  if (!text) return undefined;
  const clean = text.replace(/<\/{0,1}[a-z]+>/gi, '');
  if (clean.length > length) {
    const regex = new RegExp(`^(.{${length}}[^s]*).*`);
    return `${clean.replace(regex, '$1')}...`;
  }
  return clean;
}

export const Head = ({ title, description, seoImage, location }: HeadProps) => {
  const postTitle = title
    ? `${title} - PhenixRTS Docs`
    : 'PhenixRTS Developer Docs';
  const postDescription =
    truncate(description) ||
    'API and SDK documentation for PhenixRTS video streaming services. View helpful guides, common issues, and detailed usage examples for all of our products.';

  return (
    <Helmet>
      <title>{postTitle}</title>
      <meta id="seo-description" name="description" content={postDescription} />
      <meta
        property="og:url"
        content={`https://phenixrts.com/docs/${location?.pathname}`}
        key="ogurl"
      />
      <meta name="twitter:title" content={postTitle} key="twcard" />
      <meta name="twitter:card" content="summary_large_image" key="twcard" />
      <meta
        property="og:image"
        content={
          seoImage || 'https://phenixrts.com/docs/images/social-share.jpg'
        }
        key="ogimage"
      />
      <meta property="og:site_name" content="PhenixRTS Docs" key="ogsitename" />
      <meta property="og:title" content={postTitle} key="ogtitle" />
      <meta
        property="og:description"
        content={postDescription}
        key="ogdescription"
      />
    </Helmet>
  );
};
